import Head from "next/head";
import * as React from "react";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { getOne } from "@/api/contentful";
import Article from "@/components/Article";
import { getProducts } from "@/api/products";
import { Heading } from "@/components/Shared";
import Comparison from "@/components/Comparison";
import PageSEO from "@/components/Shared/PageSEO";
import { Article as ArticleType, Product } from "@/types/index";
import HeaderContent from "@/components/Comparison/HeaderContent";
import { HeaderContent as HeaderContentType } from "@/types/header";
import useBroadbandComparison from "@/hooks/useBroadbandComparison";
import { tvProductSelector } from "@/utils/broadbandProductSelector";
import PostcodeChecker from "@/components/Comparison/PostcodeChecker";
import { ABOUT_BROADBAND_TV, BROADBAND_TV_HEADER_CONTENT } from "@/data/consts";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Broadband",
		href: "/broadband",
		active: false,
	},
	{
		title: "Broadband and TV",
		href: "/broadband/tv",
		active: true,
	},
];

const BroadbandAndTv: React.FC<{
	products: Array<Product>;
	headerContent: HeaderContentType;
	article: ArticleType;
}> = ({ products, headerContent, article }) => {
	const broadbandComparison = useBroadbandComparison(products);

	const [sortedFilteredProducts, setSortedFilteredProducts] =
		React.useState<Array<Product>>(products);

	React.useEffect(() => {
		const applyFiltersAndSort = async () => {
			const newSortedFilteredProducts = tvProductSelector(
				broadbandComparison.products,
				broadbandComparison.contractFilters,
				broadbandComparison.networkFilters,
				broadbandComparison.speedFilters,
				broadbandComparison.sorter
			);

			setSortedFilteredProducts(newSortedFilteredProducts);
		};

		applyFiltersAndSort();
	}, [
		broadbandComparison.products,
		broadbandComparison.contractFilters,
		broadbandComparison.networkFilters,
		broadbandComparison.speedFilters,
		broadbandComparison.sorter,
	]);

	React.useEffect(() => {
		if (sortedFilteredProducts.length > broadbandComparison.viewableDeals) {
			broadbandComparison.clearFilters();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="h-100 d-flex flex-column">
			<Head>
				<link rel="canonical" href="https://www.savely.co.uk/broadband/tv" />
			</Head>
			<PageSEO
				title={"Compare Our Best Broadband and TV Deals | Consumer Choices"}
				description={
					"Compare our best Broadband and TV deals here from the major UK providers such as TalkTalk, NOW TV, Sky, Virgin Media and more! See what you could save by switching today."
				}
				keywords={"Broadband and TV Deals, TV and Broadband Deals"}
			/>
			<Header
				left={
					<PostcodeChecker
						isloading={broadbandComparison.loading}
						hasResults={broadbandComparison.postcode !== ""}
						setPostcode={broadbandComparison.handlePostcodeChange}
						resultsTitle={
							<h4 className="postcode_results_title">
								{sortedFilteredProducts.length === 0 ? (
									"Sorry, we found no "
								) : (
									<>
										We found {""}
										<span style={{ color: "#d371cc" }}>
											{sortedFilteredProducts.length}
										</span>
									</>
								)}
								{` TV and broadband deals for you in `}
								<span style={{ color: "#d371cc" }}>
									{broadbandComparison.postcode.toUpperCase()}
								</span>
							</h4>
						}
						formTitle={
							<Heading
								title={`Enter your postcode below to discover relevant TV and broadband deals based on your location!`}
								variant={4}
								isBold={false}
							/>
						}
					/>
				}
				right={<HeaderContent data={headerContent} />}
				breadcrumbs={Breadcrumbs}
			/>
			<div id="deals">
				<Comparison
					products={sortedFilteredProducts}
					broadbandComparison={broadbandComparison}
				/>
			</div>
			<Article article={article} showAuthor={false} showSideBar={false} />
			<Footer />
		</div>
	);
};

export async function getStaticProps() {
	const products = await getProducts();
	const headerContent = await getOne(BROADBAND_TV_HEADER_CONTENT);
	const article = await getOne(ABOUT_BROADBAND_TV);

	return {
		props: {
			products: products.data,
			headerContent,
			article,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
}

export default BroadbandAndTv;
